import React from 'react';
import './HealthScore.css';

/**
 * Infrastructure Health Score Component
 * Displays overall health score and breakdown by category
 */
const HealthScore = ({ healthData, showDetails = true }) => {
  if (!healthData) {
    return null;
  }

  const { overall, status, statusIcon, components, issues, criticalCount, warningCount } = healthData;

  // A score is "present" only when it's a real number. Categories with no
  // collected resources now come through as null (hasData:false) and the
  // overall is null when nothing at all was collected - render those as
  // "No data" rather than a misleading 0% or a fabricated 100.
  const hasScore = (v) => typeof v === 'number' && Number.isFinite(v);

  const getScoreColor = (score) => {
    if (!hasScore(score)) return '#9ca3af'; // Grey - no data
    if (score >= 85) return '#00A651'; // Green
    if (score >= 70) return '#FF9800'; // Orange
    return '#E53935'; // Red
  };

  const getStatusClass = (score) => {
    if (!hasScore(score)) return 'no-data';
    if (score >= 85) return 'healthy';
    if (score >= 70) return 'warning';
    return 'critical';
  };

  const statusText = (() => {
    if (status === 'no_data' || !hasScore(overall)) return 'No data collected';
    if (status === 'healthy') return 'Healthy';
    if (status === 'warning') return 'Needs Attention';
    return 'Critical Issues';
  })();

  const componentLabels = {
    compute: { label: 'Compute', icon: '💻', description: 'EC2, Lambda, ECS' },
    database: { label: 'Database', icon: '🗄️', description: 'RDS, DynamoDB, ElastiCache' },
    security: { label: 'Security', icon: '🔒', description: 'GuardDuty, Secrets, ECR' },
    network: { label: 'Network', icon: '🌐', description: 'API Gateway, CloudFront, ELB' },
    operations: { label: 'Operations', icon: '⚙️', description: 'Alarms, Step Functions, Glue' }
  };

  return (
    <div className="health-score-container">
      {/* Main Health Score */}
      <div className="health-score-main">
        <div className={`health-score-circle ${getStatusClass(overall)}`}>
          <div className="score-value">{hasScore(overall) ? Math.round(overall) : '—'}</div>
          <div className="score-label">Health Score</div>
        </div>
        <div className="health-status">
          <span className="status-icon">{statusIcon}</span>
          <span className={`status-text ${status}`}>
            {statusText}
          </span>
        </div>
        {(criticalCount > 0 || warningCount > 0) && (
          <div className="issue-summary">
            {criticalCount > 0 && (
              <span className="issue-badge critical">🔴 {criticalCount} Critical</span>
            )}
            {warningCount > 0 && (
              <span className="issue-badge warning">🟡 {warningCount} Warning</span>
            )}
          </div>
        )}
      </div>

      {/* Component Breakdown */}
      {showDetails && components && (
        <div className="health-components">
          <h4>Health Breakdown</h4>
          <div className="components-grid">
            {Object.entries(components).map(([key, data]) => {
              const present = hasScore(data.score);
              return (
                <div key={key} className={`component-card ${getStatusClass(data.score)}`}>
                  <div className="component-header">
                    <span className="component-icon">{componentLabels[key]?.icon}</span>
                    <span className="component-label">{componentLabels[key]?.label}</span>
                  </div>
                  <div className="component-score">
                    <div
                      className="score-bar"
                      style={{
                        width: present ? `${data.score}%` : '0%',
                        backgroundColor: getScoreColor(data.score)
                      }}
                    />
                    <span className="score-text">{present ? `${data.score}%` : 'No data'}</span>
                  </div>
                  <div className="component-description">
                    {componentLabels[key]?.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Issues List */}
      {showDetails && issues && issues.length > 0 && (
        <div className="health-issues">
          <h4>Active Issues ({issues.length})</h4>
          <div className="issues-list">
            {issues.slice(0, 10).map((issue, index) => (
              <div key={index} className={`issue-item ${issue.severity}`}>
                <span className="issue-severity-icon">
                  {issue.severity === 'critical' ? '🔴' : '🟡'}
                </span>
                <span className="issue-category">{issue.category}</span>
                <span className="issue-message">{issue.message}</span>
              </div>
            ))}
            {issues.length > 10 && (
              <div className="issues-more">
                +{issues.length - 10} more issues
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthScore;
